import { Injectable } from '@angular/core';
import { CognitoService } from './cognito.service';
import { Subject } from 'rxjs';
import { environment } from '../environments/environment';

// region types
/**
 * Type that defines the websocket callbacks expected by the subscription system
 */
export type WebSocketCallback = (msg: BaseMessage) => void;

/**
 * Type that defines the subscriber data dictionary
 * <br>
 * Should ***not*** be made public ('export' keyword)
 */
type WebSocketSubscriptions = {
  [messageType: string]: {
    [subscriber: string]: WebSocketCallback[];
  };
};
//endregion types

//region interfaces
/**
 * Defines the base format for a websocket Message
 */
export interface BaseMessage {
  ACTION: string;
  CONTENT: { [key: string]: any };
}

/**
 * Defines the base format used for Update messages
 */
export interface GenericUpdatesMessage extends BaseMessage {
  ACTION: string;
  CONTENT: {
    CHANGES: {
      [key: string]: any;
    }[];
  };
}

/**
 * Defines the properties found on incoming ROUTE_UPDATE messages
 */
//TODO Make it extend GenericUpdatesMessage instead of BaseMessage?
export interface RouteUpdateMessage extends BaseMessage {
  ACTION: 'ROUTE_UPDATE';
  CONTENT: {
    ROUTE_ID: string;
    ROUTE_STOP_ID: string;
    STATUS: string;
    // CHANGES: [
    //   {
    //     ROUTE_ID: string;
    //     ROUTE_STOP_ID: string;
    //     STATUS: string;
    //   },
    // ];
  };
}

export interface wsChatMessage extends BaseMessage {
  ACTION: 'MESSAGE',
  CONTENT: {
    TARGETS: string[];
    SENDER: string;
    CONVERSATION_ID: string;
    MESSAGE: string;
    MESSAGE_ID?: string;
  };
  DISPATCH:string;
}

/**
 * Defines the properties found on incoming NOTIFICATION messages
 */
export interface NotificationMessage extends BaseMessage {
  ACTION: 'NOTIFICATION';
  CONTENT: {
    ID: string;
    TITLE: string;
    BODY: string;
    // LEVEL: string,
    // REQUIRE_ACKNOWLEDGEMENT: string,
    TIMESTAMP: number;
    ICON: string;
  };
}
/**
 * Defines the properties found on incoming Read receipt messages
 */
export interface ReadReceiptMessage extends BaseMessage {
  ACTION: 'MESSAGE_READ';
  CONTENT: {
    MESSAGE_ID: string;
    READER: string;
    SENDER: string;
    CONVERSATION: string;
  };
}
/**
 * Defines the properties found on incoming Chat Updates messages
 */
export interface ChatUpdateMessage extends BaseMessage {
  ACTION: 'CHAT_UPDATES';
  CONTENT: {
    TYPE: string;
    TARGET: string;
    SENDER: string;
    CONVERSATION: string;
  };
}
/**
 * Defines the properties found on incoming user status messages
 */
export interface ChatUsersMessage extends BaseMessage {
  ACTION: 'CHAT_USERS';
  CONTENT: {
    USER_LIST: string[];
    CURRENT_USER: string;
  };
}
/**
 * Defines the properties found on incoming screen share candidate messages
 */
export interface ScreenShareCandidateMessage extends BaseMessage {
  ACTION: 'SCREEN_SHARE_CANDIDATE';
  CONTENT: {
    CANDIDATE: string;
    TARGET: string;
    SENDER: string;
  };
}
/**
 * Defines the properties found on incoming Screen share offer messages
 */
export interface ScreenShareOfferMessage extends BaseMessage {
  ACTION: 'SCREEN_SHARE_OFFER';
  CONTENT: {
    OFFER: string;
    TARGET: string;
    SENDER: string;
  };
}
/**
 * Defines the properties found on incoming Screen share offer messages
 */
export interface ScreenShareAnswerMessage extends BaseMessage {
  ACTION: 'SCREEN_SHARE_ANSWER';
  CONTENT: {
    ANSWER: string;
    TARGET: string;
    SENDER: string;
  };
}

/**
 * Defines the WebSocketSubscription object used by the subscription system
 */
export interface WebSocketSubscription {
  messageType: string;
  subscriber: string;
  callback: WebSocketCallback;
}
//endregion interfaces

/**
 * Mostly for debugging purposes, indicates that the subscription should receive every incoming messages
 */
export const ALL_MESSAGE_TYPES = '_ALL_MESSAGE_TYPES_';

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private socket!: WebSocket;
  private subscriptions: WebSocketSubscriptions = {};

  // Properties to handle the automatic reconnections
  private readonly reconnectDelay: number = 1000; // 1sec delay
  private readonly maxRetries: number = -1; // no maximum tries
  private retryCount: number = 0;
  private reconnecting: boolean = false;
  public chatUserList: any;
  private enabled: boolean = false;

  constructor(private cognitoService: CognitoService) {}

  public async init() {
    if (
      this.socket?.readyState === WebSocket.CONNECTING ||
      this.socket?.readyState === WebSocket.OPEN
    ) {
      return;
    }

    // Enable the service
    this.enabled = true;

    const USER = await this.cognitoService.getCurrentUserSub();
    this.socket = new WebSocket(environment.webSocket);
    // WS open event
    this.socket.onopen = async () => {
      this.retryCount = 0; // Reset retry count on successful connection
      this.reconnecting = false;

      // console.log('WebSocket connection established');

      let login = {
        DISPATCH: 'SESSION',
        ACTION: 'LOGIN',
        CONTENT: {
          USER,
          KEY: 'someKey',
          TYPE: 'ANGULAR_CLIENT',
        },
      };
      // Envoyer l'ID Cognito après la connexion
      this.socket?.send(JSON.stringify(login));
    };

    // WS close event
    this.socket.onclose = (event) => {
      if (event.wasClean) {
        console.warn('The connection was closed.');
      } else {
        // this console error is spamming the console
        // console.error('Connection failure : ', event); // for example, the server process is "killed"
      }

      // Skip reconnection if service is disabled
      if (!this.enabled) {
        return;
      }

      this.reconnecting = false;

      // for automatically reconnecting
      this.scheduleReconnect();
    };

    // WS message event
    this.socket.onmessage = async (event) => {
      if (typeof event.data === 'string') {
        let msg = JSON.parse(event.data) as BaseMessage;
        let messageType = msg.ACTION as string;
        // Take all those subscribed to messageType
        let subscribers = this.subscriptions[messageType] ?? {};
        // Add callbacks that listen to all message types (normally used for debugging)
        subscribers = {
          ...subscribers,
          ...(this.subscriptions[ALL_MESSAGE_TYPES] ?? {}),
        };
        // Iterate over all subscribers, and call all their callbacks
        for (let subscriber in subscribers) {
          subscribers[subscriber].forEach((func) => func(msg));
        }
      } else console.warn('Invalid data format from server', event.data);
    };

    // WS error event
    this.socket.onerror = (error: any) => {
      console.error('Error', error);
      // onclose will always run after onerror returns
      // But still close manually to make sure onclose runs
      this.close();
    };
  }

  public cleanUp(): void {
    this.chatUserList = [];

    // Disable the service
    this.enabled = false;

    // Close socket
    this.close();
  }

  /**
   * Closes the connection
   */
  close() {
    this.socket?.close();
  }

  /**
   * Setups and handles to auto-reconnect behaviour when the connections fails
   * @private
   */
  private scheduleReconnect() {
    // Prevent reconnects if service is disabled
    if (!this.enabled) {
      return;
    }

    // Prevents reconnection in login page
    if (window.location.href.includes('login')) {
      return;
    }
    // Skip if already connected/connecting
    if (
      this.socket?.readyState === WebSocket.CONNECTING ||
      this.socket?.readyState === WebSocket.OPEN ||
      this.reconnecting
    ) {
      return;
    }

    // Check if we reached max attempts
    if (this.maxRetries != -1 && this.retryCount >= this.maxRetries) {
      console.error(
        'Maximum connection attempt reached. WebSocket failed to connect too many times.'
      );
    }

    this.reconnecting = true;
    this.retryCount++;
    setTimeout(async () => {
      // console.log(`Reconnecting attempt #${this.retryCount}`);
      await this.init();
    }, this.reconnectDelay);
  }

  /**
   * Send the Message to the websocket server
   * @param message
   */
  public send(message: BaseMessage) {
    if (!(this.socket && this.socket.readyState === WebSocket.OPEN)) {
      console.warn('WebSocket is not open. Unable to send message.');
      return;
    }
    const jsonMessage = JSON.stringify(message);
    this.socket.send(jsonMessage);
  }

  /**
   * Allows services/components to subscribe to specific incoming message types
   * @param subscription
   */
  public subscribe(subscription: WebSocketSubscription) {
    let subscriber = subscription.subscriber;
    let messageType = subscription.messageType;

    if (!this.subscriptions[messageType]) {
      this.subscriptions[messageType] = {};
    }
    if (!this.subscriptions[messageType][subscriber]) {
      this.subscriptions[messageType][subscriber] = [];
    }
    this.subscriptions[messageType][subscriber].push(subscription.callback);
  }

  /**
   * Allows services/components to unsubscribe from specific incoming message types
   * This prevents keeping unnecessary callback references
   * @param subscription
   */
  public unsubscribe(subscription: WebSocketSubscription): void {
    let subscriber = subscription.subscriber;
    let messageType = subscription.messageType;

    if (
      this.subscriptions[messageType] &&
      this.subscriptions[messageType][subscriber]
    ) {
      delete this.subscriptions[messageType][subscriber];

      // Clean up the messageType entry if it has no more subscribers
      if (Object.keys(this.subscriptions[messageType]).length === 0) {
        delete this.subscriptions[messageType];
      }
    }
  }
  public async getContactListStatus(subList: any) {
    this.chatUserList = subList;
    const STATUS = '_available'
    let wsGetStatusMessage = {
      ACTION: 'USERS',
      CONTENT: {
        CHAT_USERS: subList,
        CURRENT_USER: await this.cognitoService.getCurrentUserSub(),
        STATUS,
        
      },
      DISPATCH: 'Chat'
    };
    this.socket.send(JSON.stringify(wsGetStatusMessage));
  }
  public async sendLogOutNotif() {
    let wsLogOutStatus = {
      ACTION: 'LOG_OUT',
      CONTENT: {
        chatUsers: this.chatUserList,
        currentUser: await this.cognitoService.getCurrentUserSub(),
      },
    };
    return this.socket.send(JSON.stringify(wsLogOutStatus));
  }

  public checkWState(): boolean {
    if (
      this.socket?.readyState === WebSocket.CONNECTING ||
      this.socket?.readyState === WebSocket.OPEN ||
      this.socket
    ) {
      return false;
    }
    return true;
  }
}

/**
 * Class holding Type Guards for WebSocket message types, to help differentiate them from BaseMessage
 * <br>
 * These have the added bonus of enabling proper autocomplete from IDEs
 */
export class WebSocketTypeGuards {
  public static isNotificationMsg(
    msg: BaseMessage
  ): msg is NotificationMessage {
    return msg.ACTION === 'NOTIFICATION';
  }
  public static isRouteUpdateMsg(msg: BaseMessage): msg is RouteUpdateMessage {
    return msg.ACTION === 'ROUTE_UPDATE';
  }
  public static isChatMsg(msg: BaseMessage): msg is wsChatMessage {
    return msg.ACTION === 'CHAT_MESSAGE';
  }
  public static isReadReceiptMsg(msg: BaseMessage): msg is ReadReceiptMessage {
    return msg.ACTION === 'MESSAGE_READ';
  }
  public static isChatUpdateMsg(msg: BaseMessage): msg is ChatUpdateMessage {
    return msg.ACTION === 'CHAT_UPDATES';
  }
  public static isChatUsersMsg(msg: BaseMessage): msg is ChatUsersMessage {
    return msg.ACTION === 'CHAT_USERS';
  }
  public static isScreenShareCandidateMsg(
    msg: BaseMessage
  ): msg is ScreenShareCandidateMessage {
    return msg.ACTION === 'SCREEN_SHARE_CANDIDATE';
  }
  public static isScreenShareOfferMessageMsg(
    msg: BaseMessage
  ): msg is ScreenShareOfferMessage {
    return msg.ACTION === 'SCREEN_SHARE_OFFER';
  }
  public static isScreenShareAnswerMsg(
    msg: BaseMessage
  ): msg is ScreenShareAnswerMessage {
    return msg.ACTION === 'SCREEN_SHARE_ANSWER';
  }
}
